import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import translate from '../../../commonComponents/translate';
import withCSRF from '../../../commonComponents/withCSRF';
import Anchor from '../../../commonComponents/Anchor';
import UserProp from '../../../commonComponents/propTypes/userProp';
import CartStateProp from '../propTypes/cartStateProp';
import ShopShippingAddress from '../shopItems/ShopShippingAddress';
import FormAddress from '../../../commonComponents/forms/Address/FormAddress';
import Notifications from '../../../services/notifications';
import { compose, formatPrice } from '../../../services/utilityFunctions';
import CountryProp from '../../../commonComponents/forms/Address/CountryProp';
import Form from '../../../commonComponents/forms/Form';
import Loader from '../../../services/loader';
import organizationProp from '../../../commonComponents/propTypes/organizationProp';
import MarketInfosContext from '../../../contextProviders/MarketInfosContext';
import FormValidation from '../../../services/formValidation';
import VatTypesProp from '../../../commonComponents/forms/Address/VatTypesProp';
import CustomRadio from '../../../commonComponents/forms/inputs/CustomRadio';
import MondialRelay from '../../../commonComponents/modialRelay/MondialRelay';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import i18n from '../../../services/i18n';
import TenantProp from '../../../commonComponents/propTypes/tenantProp';
import BillingPaymentOptions, {
  BILLING_OPTIONS,
} from '../../../commonComponents/shop/BillingPaymentOptions';
import { getProductsForOrder } from '../shopHelper';

const emptyAddress = {
  address: '',
  address_1: '',
  address_2: '',
  city: '',
  country: '',
  countryCode: '',
  county: '',
  number: '',
  region: '',
  state: '',
  zipCode: '',
};
const INDIVIDUAL_PROFILE = 'individual';
const calculateVat = (
  userId,
  vatStandard,
  businessCountry,
  businessType,
  countryCode,
) =>
  new Promise((resolve, reject) => {
    const params = {
      standardVat: vatStandard,
      billingCountry: businessCountry,
      billingType: businessType,
      userCountry: countryCode,
    };
    axios
      .get(`/api/vat/tax/${userId}`, {
        params,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'License',
        },
      })
      .then(({ data: response }) => {
        resolve(response.data.standardVat);
      })
      .catch(err => {
        console.log(err);
        reject(err);
      });
  });
const returnAddress = addressParam => {
  let addressComposed = '';
  if (addressParam.address_1) {
    addressComposed += addressParam.address_1;
  }
  if (addressParam.address_2) {
    addressComposed += `, ${addressParam?.address_2}`;
  }
  if (addressParam.zipCode) {
    addressComposed += `, ${addressParam?.zipCode}`;
  }
  if (addressParam.city) {
    addressComposed += `, ${addressParam?.city}`;
  }
  if (addressParam.country) {
    addressComposed += `, ${addressParam?.country}`;
  }
  return addressComposed;
};

function ShopProductCheckoutShipping({
  t,
  goToPayment,
  goBack,
  _csrf,
  user,
  countries,
  cartState,
  updateSelectedShippingProvider,
  updateSelectedAddress,
  organization,
  orderDetails,
  checkIfShippingIsDifferent,
  vatTypes,
  updateUser,
  updatePickup,
  isOnlyDigitalProducts,
  orderAlreadyCreated,
  isShippingDifferent,
  tenant,
  handleBillingChange,
  billingActive,
  errors,
  companyEmails,
  onCompanyEmailsChange,
  onCompanyBillingChange,
  onCompanyErrors,
  isSubscription,
}) {
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const loaderAddresses = useRef(new Loader('#addressesContainer'));
  useEffect(() => {
    if (loaderAddresses.current) {
      if (isLoadingAddresses) {
        loaderAddresses.current.show();
      } else {
        loaderAddresses.current.hide();
      }
    }
  }, [isLoadingAddresses]);
  // Key for mondial relay if exists
  const customerKey =
    organization?.services?.mondial_relay?.config?.customerCode || null;

  const { personalInfo } = user;
  const businessType = personalInfo?.businessType || null;
  const isIndividual = businessType === 'individual' || !businessType;
  const companyName = personalInfo?.companyName || null;

  const [editingAddressIndex, setEditingAddressIndex] = useState(null);
  const [editingAddress, setEditingAddress] = useState(null);

  const [billingAddress, setBillingAddress] = useState(
    cartState?.billingAddress
      ? {
          ...cartState?.billingAddress,
          firstName: cartState?.billingAddress?.firstName || user.firstName,
          lastName: cartState?.billingAddress?.lastName || user.lastName,
          email: cartState?.billingAddress?.email || user.email,
          phone: cartState?.billingAddress?.phone || user.phone,
          companyName: !isIndividual
            ? cartState?.billingAddress?.companyName || companyName
            : '',
        }
      : {
          ...user?.address,
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          email: user?.email,
          phone: user?.phone,
          companyName: !isIndividual ? companyName || '' : '',
        },
  );

  // Only filter if is individual profile
  const initialShippingAddressState =
    !businessType || businessType === INDIVIDUAL_PROFILE
      ? user.shippingAddress.filter(
          address => address.countryCode === user.shippingCountry,
        )
      : user.shippingAddress;
  const [shippingAddresses, setShippingAddresses] = useState(
    initialShippingAddressState || [],
  );
  const [checkedAddresses, setCheckedAddresses] = useState({
    billing: 0,
    shipping: 1,
  });
  const [shippingAddressIsDifferent, setShippingAddressIsDifferent] =
    useState(isShippingDifferent);
  const [providersList, setProvidersList] = useState([]);

  useEffect(() => {
    checkIfShippingIsDifferent(shippingAddressIsDifferent);
  }, [shippingAddressIsDifferent]);

  /** CHECK USER VAT AT THE START TO DETECT CHANGES WHEN SAVING ADDRESS */
  const [checkUserVat, setCheckUserVat] = useState(null);
  const vatStandard =
    organization?.marketPlace
      ?.find(m => m.isDefault)
      ?.vatRates.find(vat => vat.type === 'standard').percentage || 17;
  const businessCountry = organization?.marketPlace?.country;

  let userCountry = user?.address?.countryCode || null;
  useEffect(() => {
    if (vatStandard && businessCountry && businessType && userCountry) {
      calculateVat(
        user._id,
        vatStandard,
        businessCountry,
        businessType,
        userCountry,
      ).then(vat => setCheckUserVat(vat));
    }
  }, []);

  const loader = useRef(new Loader('#shippingOption'));
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  const handleSelectShipping = providerId => () => {
    updateSelectedShippingProvider(providerId);
    updatePickup(null);
  };

  const calculateShipping = (billing, shipping, products) => {
    const params = {
      billingAddress: billing,
      shippingAddress: shipping || billing,
      products: getProductsForOrder(products),
      country: user.shippingCountry,
      currency: user.currency,
    };
    if (params?.billingAddress && params?.shippingAddress) {
      setIsLoading(true);

      axios
        .post('/api/market/shipping-provider/calculate-shipping', params, {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        })
        .then(({ data }) => {
          setProvidersList(data.data);
          if (
            cartState?.selectedShippingProvider?._id &&
            !data.data.find(
              p => p._id === cartState?.selectedShippingProvider?._id,
            )
          ) {
            updateSelectedShippingProvider(-1);
          }
          setIsLoading(false);
        })
        .catch(err => {
          Notifications.showErrorFromRequest(err, 'error_calculateShipping');
          console.log('Err', err);
        });
    }
  };

  useEffect(() => {
    if (!isOnlyDigitalProducts) {
      if (
        (cartState.billingAddress ||
          cartState.shippingAddress ||
          billingAddress ||
          shippingAddresses.length) &&
        cartState.products.length !== 0
      ) {
        const defaultShippingAddresses =
          shippingAddresses.length > 0 ? shippingAddresses[0] : billingAddress;
        calculateShipping(
          cartState.billingAddress || billingAddress || {},
          shippingAddressIsDifferent
            ? cartState.shippingAddress || defaultShippingAddresses
            : cartState.billingAddress || billingAddress,
          cartState.products,
        );
      }
    }
  }, []);

  // use effect responsible to calculate the shipping provider
  // when there is updates in
  // checkbox "Have a different shipping address"
  // Delete a shipping address
  // and select different addresses
  useEffect(() => {
    if (!isOnlyDigitalProducts) {
      if (
        cartState.billingAddress &&
        cartState.products.length > 0 &&
        billingActive === BILLING_OPTIONS.YOUR_BILLING
      ) {
        if (
          shippingAddressIsDifferent &&
          shippingAddresses[checkedAddresses.shipping - 1]
        ) {
          calculateShipping(
            cartState.billingAddress || billingAddress,
            shippingAddresses[checkedAddresses.shipping - 1] ||
              cartState.shippingAddress,
            cartState.products,
          );
        } else if (
          !shippingAddressIsDifferent ||
          checkedAddresses.shipping === -1 ||
          shippingAddresses[checkedAddresses.shipping - 1] === undefined
        ) {
          calculateShipping(
            cartState.billingAddress,
            cartState.billingAddress,
            cartState.products,
          );
        }
      }

      if (
        billingActive === BILLING_OPTIONS.COMPANY_BILLING &&
        cartState.products.length > 0 &&
        cartState?.companyBilling?.address?.countryCode
      ) {
        calculateShipping(
          cartState?.companyBilling?.address,
          shippingAddresses[checkedAddresses.shipping - 1] ||
            cartState.shippingAddress,
          cartState.products,
        );
      }
    }
  }, [
    billingAddress?.countryCode,
    cartState.products,
    cartState?.companyBilling?.address?.countryCode,
    checkedAddresses,
    shippingAddressIsDifferent,
    shippingAddresses,
  ]);
  const [isShipping, setIsShipping] = useState(false);
  const handleAddNewAddress = (isShippingAddress = false) => {
    setIsShipping(isShippingAddress);
    setEditingAddressIndex(-1);
    setEditingAddress({
      ...emptyAddress,
      countryCode: user.shippingCountry,
      country: countries.find(c => c.code === user.shippingCountry).name,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
    });
  };

  const [showYourBillingAddress, setShowYourBillingAddress] = useState(false);
  const editAddress = billing => index => {
    setIsShipping(!billing);
    const address = billing ? billingAddress : shippingAddresses[index - 1];
    setEditingAddressIndex(index);
    setEditingAddress(address || emptyAddress);
  };

  const setChecked = billing => id => {
    setCheckedAddresses(c => {
      const checkedVal = billing ? 'billing' : 'shipping';
      updateSelectedShippingProvider(-1); // Resets the shipping Provider
      return {
        ...c,
        [checkedVal]: id,
      };
    });
  };

  const handleYourBilling = field => v => {
    setBillingAddress(prev => ({
      ...prev,
      [field]: v,
    }));
  };

  const handleFieldChange = field => v => {
    setEditingAddress(prev => ({
      ...prev,
      [field]: v,
    }));
  };

  const handleDifferentShippingAddress = event => {
    const {
      target: { checked },
    } = event;
    let shippingA;
    if (!checked && checkedAddresses.billing !== null) {
      shippingA = billingAddress;
    } else if (checked && checkedAddresses.shipping) {
      shippingA = shippingAddresses[checkedAddresses.shipping - 1];
    } else {
      shippingA =
        checkedAddresses.shipping !== null
          ? shippingAddresses[checkedAddresses.shipping]
          : null;
    }
    setShippingAddressIsDifferent(checked);
    updateSelectedAddress({
      billing: checkedAddresses.billing === 0 ? billingAddress : null,
      shipping: shippingA,
    });
    updateSelectedShippingProvider(-1); // Resets the shipping provider
  };

  const saveAddress = () => {
    try {
      setIsLoadingAddresses(true);
      setShowYourBillingAddress(false);
      const updatingAddresses = {
        billing: { ...billingAddress },
        shipping: [...shippingAddresses],
      };

      if (editingAddressIndex === 0) {
        setBillingAddress(editingAddress);
        updatingAddresses.billing = editingAddress;
      } else if (editingAddressIndex === -1) {
        setShippingAddresses([...shippingAddresses, editingAddress]);
        updatingAddresses.shipping = [...shippingAddresses, editingAddress];
      } else {
        setShippingAddresses(
          shippingAddresses.map((s, idx) =>
            idx === editingAddressIndex - 1 ? editingAddress : s,
          ),
        );
        updatingAddresses.shipping = shippingAddresses.map((s, idx) =>
          idx === editingAddressIndex - 1 ? editingAddress : s,
        );
      }

      let billing = null;
      if (
        updatingAddresses.billing &&
        updatingAddresses.billing.address_1 &&
        updatingAddresses.billing.countryCode
      ) {
        billing = {
          ...updatingAddresses.billing,
          address: returnAddress(updatingAddresses.billing),
        };
      }
      const params = {
        address: billing,
        shippingAddress: updatingAddresses.shipping,
        _csrf,
      };
      let userToUpdate = {};
      if (params?.address?.firstName) {
        userToUpdate.firstName = params.address.firstName;
      }
      if (params?.address?.lastName) {
        userToUpdate.lastName = params.address.lastName;
      }
      if (params?.address?.email) {
        userToUpdate.email = params.address.email.toLowerCase();
      }
      if (params?.address?.phone) {
        userToUpdate.phone = params.address.phone;
      }
      if (params?.address?.companyName) {
        userToUpdate = {
          ...userToUpdate,
          personalInfo: {
            ...user.personalInfo,
            companyName: params.address.companyName,
          },
        };
      }
      axios({
        url: '/api/user/me',
        method: 'PUT',
        data: { ...params, ...userToUpdate },
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Organization',
        },
      })
        .then(({ data: response }) => {
          updateUser(response.data);
          const existShipping = response.data.shippingAddress.length > 0;
          const currentShippingAddress = cartState?.shippingAddress?.id;
          const defaultShippingAddress =
            response.data.shippingAddress.find(
              s => s.id === currentShippingAddress,
            ) ||
            response.data.shippingAddress[0] ||
            response.data.address;

          if (
            billingActive === BILLING_OPTIONS.COMPANY_BILLING &&
            existShipping
          ) {
            updateSelectedAddress({
              billing: response.data.address,
              shipping: defaultShippingAddress,
            });
          }

          if (
            billingActive === BILLING_OPTIONS.YOUR_BILLING &&
            response?.data?.address
          ) {
            let selectedShipping = response.data.address;

            if (existShipping && shippingAddressIsDifferent) {
              selectedShipping = defaultShippingAddress;
            }

            if (editingAddressIndex === -1 && existShipping) {
              selectedShipping =
                response.data.shippingAddress[
                  response?.data?.shippingAddress?.length - 1
                ];
            }

            updateSelectedAddress({
              billing: response.data.address,
              shipping: selectedShipping,
            });
            setBillingAddress(response.data.address);
            setShippingAddresses(response?.data?.shippingAddress);
            setIsLoadingAddresses(false);
            // After the address is saved we will confirm if vat for user changed, if so
            // page will reload so the products and shipping get the correct vat
            userCountry = response.data.address.countryCode;
            if (
              // Check if country code is different or not
              user?.address?.countryCode !==
                response.data.address.countryCode &&
              vatStandard &&
              businessCountry &&
              businessType &&
              userCountry
            ) {
              calculateVat(
                user._id,
                vatStandard,
                businessCountry,
                businessType,
                userCountry,
              )
                .then(standardVat => {
                  if (standardVat !== checkUserVat) {
                    Notifications.showNotificationWarning(
                      t('warning'),
                      t('warning.updating.vatValues'),
                    );
                    window.location.reload();
                  }
                })
                .catch(err => console.log(err));
            }
          }
        })
        .catch(err => {
          Notifications.showErrorFromRequest(err, 'error_fetchProductsList');
          console.log(err);
        });
      setEditingAddressIndex(null);
    } catch (err) {
      console.error(err);
      setIsLoadingAddresses(false);
    }
  };

  useEffect(() => {
    if (billingActive === BILLING_OPTIONS.YOUR_BILLING) {
      let shippingA;
      if (!shippingAddressIsDifferent && checkedAddresses.billing !== null) {
        shippingA = billingAddress;
      } else {
        shippingA =
          checkedAddresses.shipping !== null
            ? shippingAddresses[checkedAddresses.shipping - 1]
            : null;
      }
      updateSelectedAddress({
        billing: checkedAddresses.billing === 0 ? billingAddress : null,
        shipping: checkedAddresses.shipping !== -1 ? shippingA : billingAddress,
      });
    }
  }, [checkedAddresses]);

  const selectedShippingProvider = cartState.selectedShippingProvider
    ? cartState.selectedShippingProvider._id
    : null;

  const removeAddress = index => {
    shippingAddresses.splice(index - 1, 1);
    setShippingAddresses(shippingAddresses);
    updateSelectedShippingProvider(-1); // Resets the shipping provider
    saveAddress();
  };

  const [isMondialRelay, setIsMondialRelay] = useState(false);

  // Disables the button continue
  const disableContinueBtnBillingAddress =
    (cartState.billingAddress &&
      cartState.billingAddress.countryCode &&
      cartState.billingAddress.address_1 &&
      (cartState?.selectedShippingProvider?.userPrice?.price >= 0 ||
        cartState?.selectedShippingProvider?.userClickAndCollectPrice?.price >=
          0)) ||
    (cartState.billingAddress &&
      cartState.billingAddress.countryCode &&
      cartState.billingAddress.address_1 &&
      cartState.shippingAddress &&
      cartState.shippingAddress.countryCode &&
      cartState.shippingAddress.address_1 &&
      (cartState.selectedShippingProvider?.userPrice?.price >= 0 ||
        cartState?.selectedShippingProvider?.userClickAndCollectPrice?.price >=
          0));

  const disableBtn =
    billingActive === BILLING_OPTIONS.YOUR_BILLING
      ? disableContinueBtnBillingAddress
      : true;
  const disabledBtn = isMondialRelay
    ? cartState?.pickupInfo?.pickupId?.length > 0 && disableBtn
    : disableBtn;

  const disableIfDigital =
    (isOnlyDigitalProducts && cartState.billingAddress) ||
    billingActive === BILLING_OPTIONS.COMPANY_BILLING;
  // Shows an empty string when the selected shipping provider is undefined (reseted)
  const renderMessage = () => {
    let msg = '';
    if (cartState.selectedShippingProvider === null) {
      msg = t('shop.cart.provider_not_delivery');
    }
    if (
      cartState?.selectedShippingProvider?._id &&
      !cartState?.selectedShippingProvider?.userPrice?.price
    ) {
      msg = t('shop.cart.provider_not_delivery');
    }
    return msg;
  };

  const displayedPrice = orderDetails.shippingSubTotal;
  const { loggedUser } = useContext(LoggedUserContext);
  // Don't allow the user to continue without a shipping address if billing address is not equal to shipping country
  const { marketInfos } = useContext(MarketInfosContext);
  const handleGoToPayment = () => {
    if (onCompanyErrors()) return;
    if (billingActive === BILLING_OPTIONS.YOUR_BILLING) {
      // user don't have phone number && not have in address already saved one
      if (
        (!loggedUser?.phone || loggedUser?.phone?.length === 0) &&
        (!cartState?.billingAddress?.phone ||
          cartState?.billingAddress?.phone?.length === 0)
      ) {
        Notifications.showNotificationError(
          t('error'),
          i18n.__('marketplace.shippingTab.error.phone'),
        );
        return;
      }
    }
    // If businessType === 'individual' (not company)
    // or is null, the user must have a address valid according to the shipping country
    const countryToCheck = loggedUser
      ? loggedUser.shippingCountry
      : marketInfos.localCountry;
    if (!businessType || businessType === INDIVIDUAL_PROFILE) {
      if (
        (cartState?.shippingAddress?.countryCode &&
          marketInfos?.localCountry &&
          cartState?.shippingAddress?.countryCode !== countryToCheck) || // If shipping different from shipping country
        (!cartState.shippingAddress && // If !shippingAddress check if billing country code is different shipping country
          cartState?.billingAddress?.countryCode !== countryToCheck)
      ) {
        Notifications.showNotificationError(
          t('error'),
          t('error_notValidShipping'),
        );
        return;
      }
    }
    const {
      shippingAddress,
      products,
      billingAddress: userAddress,
    } = cartState;
    if (
      billingActive === BILLING_OPTIONS.YOUR_BILLING &&
      userAddress &&
      (!userAddress.firstName || !userAddress.lastName)
    ) {
      Notifications.showNotificationError(
        t('error'),
        t('shop.cart.error.address'),
      );
      return;
    }
    const { countryCode } = shippingAddress || userAddress;

    const checkAvailabilityOfProducts = products.map(prod => {
      const { availableCountries, id } = prod;
      let isAvailable = true;
      if (availableCountries && availableCountries.length > 0) {
        isAvailable = !!availableCountries.find(
          available => available.toLowerCase() === countryCode.toLowerCase(),
        );
      }
      return !isAvailable ? id : null;
    });

    if (
      checkAvailabilityOfProducts.length > 0 &&
      checkAvailabilityOfProducts.find(a => a !== null)
    ) {
      goBack('shipping', checkAvailabilityOfProducts);
      return;
    }
    goToPayment();
  };
  const stateVatType = vatTypes.find(
    tax => tax.taxCode === user?.personalInfo?.taxCode,
  );

  useEffect(() => {
    FormValidation.config();
    let configs = {
      rules: {
        address_1: { required: true },
        city: { required: true },
        zipCode: { required: true },
        phone: { maxlength: 20 },
      },
      errorPlacement: (error, element) => {
        error.insertAfter(element);
      },
    };
    if (
      user &&
      editingAddressIndex === 0 &&
      stateVatType &&
      user?.personalInfo?.businessType !== 'individual' &&
      user?.personalInfo?.businessType !== ''
    ) {
      configs = {
        rules: {
          address_1: { required: true },
          city: { required: true },
          zipCode: { required: true },
          phone: { maxlength: 20 },
          countryCode: {
            required: true,
            ifDifferent: stateVatType.countryCode,
          },
        },
        messages: {
          countryCode: {
            ifDifferent: t(
              'profile.contacts.error.billingAddress.countryCode',
              stateVatType.countryName,
            ),
          },
        },
        errorPlacement: (error, element) => {
          error.insertAfter(element);
        },
      };
    }
    FormValidation.init('#formAddress', configs);
  }, [editingAddressIndex]);

  useEffect(() => {
    if (cartState && cartState.selectedShippingProvider) {
      setIsMondialRelay(
        cartState.selectedShippingProvider.providerType === 'mondial_relay',
      );
    }
  }, [cartState.selectedShippingProvider]);

  const handlePickupPoint = pickupId => {
    updatePickup(pickupId);
  };
  const renderPickupInfo = () => {
    const { pickupInfo } = cartState;
    const { address_1, address_2, city, zipCode, countryCode, firstName } =
      pickupInfo || {};
    const country = countries.find(
      c => c.code.toUpperCase() === countryCode.toUpperCase(),
    ).name;

    return (
      <div className="address-step pickup-address custom-title">
        <h3>{t('shop.cart.pickupAddress')}</h3>
        <ShopShippingAddress
          address={{
            address_1: address_1 || '',
            address_2: address_2 || '',
            city: city || '',
            zipCode: zipCode || '',
            firstName: firstName || '',
            country,
          }}
          id="pickup"
          checked
        />
      </div>
    );
  };
  const showDifferentShipping =
    (!isOnlyDigitalProducts &&
      cartState.billingAddress &&
      cartState.billingAddress.countryCode &&
      cartState.billingAddress.address_1) ||
    billingActive === BILLING_OPTIONS.COMPANY_BILLING;

  const handleBillingAddressForm = save => {
    if (!save) {
      setBillingAddress(cartState?.billingAddress || user.address);
    }
    setShowYourBillingAddress(save);
  };
  return (
    <div className="m-portlet--rounded m-portlet--rounded-force m--margin-top-10 m--margin-bottom-10 shop-checkout-step2">
      <div id="shippingTab" className="m-portlet__body">
        <div className="row no-padding">
          <div className="col-lg-8 col-md-8">
            <div
              id="addressesContainer"
              className="address-step line-list custom-title"
            >
              <h3>{t('shop.cart.billing')}</h3>
              <BillingPaymentOptions
                billingAddress={{ ...billingAddress }}
                handleAddressChange={handleYourBilling}
                orderAlreadyCreated={orderAlreadyCreated}
                tenant={tenant}
                showAddressForm={showYourBillingAddress}
                saveAddress={saveAddress}
                countries={countries}
                user={loggedUser}
                handleAddressForm={value => handleBillingAddressForm(value)}
                vatTypes={vatTypes}
                billingActive={billingActive}
                onBillingChange={handleBillingChange}
                onCompanyBillingChange={onCompanyBillingChange}
                companyBilling={cartState.companyBilling}
                errors={errors}
                companyEmails={companyEmails}
                onCompanyEmailsChange={onCompanyEmailsChange}
                isSubscription={isSubscription}
              />
              {showDifferentShipping && (
                <>
                  {billingActive === BILLING_OPTIONS.YOUR_BILLING && (
                    <div
                      className={`col-form-label shipping-ask ${
                        orderAlreadyCreated ? 'checkout-disabled' : ''
                      }`}
                    >
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control,jsx-a11y/label-has-for */}
                      <label className="m-checkbox m-checkbox--state-success">
                        <input
                          type="checkbox"
                          disabled={orderAlreadyCreated}
                          checked={shippingAddressIsDifferent}
                          onChange={handleDifferentShippingAddress}
                        />
                        {t('shop.cart.different_address')}
                        <span />
                      </label>
                    </div>
                  )}
                  <br />
                  {(shippingAddressIsDifferent ||
                    billingActive === BILLING_OPTIONS.COMPANY_BILLING) && (
                    <div className="custom-title">
                      <h3>
                        {t('shop.cart.shipping')}
                        <br />
                        <span className="shipping-address-hint reset-text">{` (${t(
                          'shop.cart.shippingCountry.hint',
                        )})`}</span>
                      </h3>
                      <ul>
                        {shippingAddresses?.map((address, idx) => (
                          <ShopShippingAddress
                            isShipping
                            address={address}
                            editAddress={editAddress(false)}
                            removeAddress={removeAddress}
                            id={idx + 1}
                            key={`shippingList-${idx}`}
                            user={user}
                            checked={
                              cartState?.shippingAddress?._id === address?._id
                            }
                            setChecked={setChecked(false)}
                            orderAlreadyCreated={orderAlreadyCreated}
                          />
                        ))}
                      </ul>
                      {!orderAlreadyCreated && (
                        <div className="add-new-btn">
                          <Anchor
                            onClick={() => handleAddNewAddress(true)}
                            className="btn btn-outline-metal m-btn m-btn--icon
                           m-btn--icon-only m-btn--custom m-btn--outline-2x
                           m-btn--pill m-btn--air"
                          >
                            <i className="fa fa-plus" />{' '}
                            <span className="shop-addNewAddress">
                              {t('shop.cart.add_new_address')}
                            </span>
                          </Anchor>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
              {cartState.pickupInfo && renderPickupInfo()}
              {editingAddressIndex !== null && (
                <div className="add-new-address">
                  <Form
                    id="formAddress"
                    submitMethod={saveAddress}
                    validationRules={{}}
                  >
                    {submitHandler => (
                      <>
                        <FormAddress
                          address={editingAddress}
                          onFieldChange={handleFieldChange}
                          countries={countries}
                          isShopCheckout
                          countryToFilter={
                            !businessType || businessType === 'individual'
                              ? editingAddress.countryCode ||
                                user.shippingCountry
                              : null
                          }
                          isShippingAddress={isShipping}
                          user={user}
                        />
                        <br />
                        <Anchor
                          className="landing-certification-browse btn m-btn--pill btn-dynamic"
                          onClick={submitHandler}
                        >
                          {t('save')}
                        </Anchor>
                        <Anchor
                          className="landing-certification-browse btn m-btn--pill btn-danger"
                          onClick={() => setEditingAddressIndex(null)}
                        >
                          {t('cancel')}
                        </Anchor>
                      </>
                    )}
                  </Form>
                </div>
              )}
            </div>
            {isMondialRelay && (
              <div className="address-step line-list custom-title pickup-location">
                <div className="">
                  <h3>{t('shop.cart.pickupLocation')}</h3>
                  <div>
                    <MondialRelay
                      onPickupSelection={handlePickupPoint}
                      config={{
                        Country:
                          cartState?.shippingAddress?.countryCode?.toUpperCase(),
                        AllowedCountries: [
                          cartState?.shippingAddress?.countryCode?.toUpperCase(),
                        ],
                        PostCode: cartState?.shippingAddress?.zipCode,
                        Brand: customerKey,
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div
            className="col-lg-4 col-md-4 continue-colum"
            id="shippingOptionContainer"
          >
            {!isOnlyDigitalProducts && (
              <div id="shippingOption" className="type-shipping">
                <h3>{`${t('shop.cart.choose_delivery')}:`}</h3>
                <ul>
                  {!isLoading &&
                    providersList.map(provider => (
                      <div key={provider._id}>
                        <li className="address-item">
                          <div className="form-group row">
                            <div className="col-9">
                              <div className="col-form-label">
                                <div className="row shipping-provider-options">
                                  <div
                                    className={`col-2 no-padding ${
                                      orderAlreadyCreated
                                        ? 'checkout-disabled'
                                        : ''
                                    }`}
                                  >
                                    <CustomRadio
                                      onChange={handleSelectShipping(provider)}
                                      checked={
                                        selectedShippingProvider ===
                                        provider._id
                                      }
                                      id={provider._id}
                                      disabled={orderAlreadyCreated}
                                    />
                                  </div>
                                  <div className="col-10 no-padding">
                                    <p>
                                      <span className="provider-name">{`${provider.name}`}</span>
                                      <br />
                                      <span>{` (${t(
                                        'shop.product.without.tax',
                                      )})`}</span>
                                    </p>
                                    {provider?.description?.length > 0 && (
                                      <span>{`${provider.description}`}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    ))}
                  {providersList.length === 0 &&
                    (cartState.billingAddress || cartState.shippingAddress) &&
                    t('shop.cart.providers_not_delivery')}
                </ul>
                {cartState.selectedShippingProvider !== null ? (
                  <div>
                    <hr />
                    <h3 style={{ textAlign: 'right' }}>
                      <span className="items-price">
                        {cartState?.selectedShippingProvider?.userPrice
                          ?.price >= 0 ||
                        cartState?.selectedShippingProvider
                          ?.userClickAndCollectPrice?.price >= 0
                          ? formatPrice(displayedPrice, orderDetails.symbol)
                          : renderMessage()}
                      </span>
                    </h3>
                  </div>
                ) : null}
              </div>
            )}
            <button
              type="button"
              className={`landing - certification - browse btn m-btn--pill btn-dynamic
                ${
                  (isOnlyDigitalProducts ? !disableIfDigital : !disabledBtn) &&
                  'disabled'
                } ${isLoading && 'm-loader m-loader--right m-loader--light'}`}
              disabled={
                isOnlyDigitalProducts ? !disableIfDigital : !disabledBtn
              }
              onClick={handleGoToPayment}
            >
              {t('continue')}
            </button>
            <Anchor
              className="btn m-btn--pill btn-metal shop-btnBack"
              onClick={() => {
                goBack('shipping');
              }}
            >
              {t('back')}
            </Anchor>
          </div>
        </div>
      </div>
    </div>
  );
}

ShopProductCheckoutShipping.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  goToPayment: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  user: PropTypes.shape(UserProp.propType).isRequired,
  cartState: PropTypes.shape(CartStateProp.propType).isRequired,
  updateSelectedShippingProvider: PropTypes.func.isRequired,
  updatePickup: PropTypes.func.isRequired,
  updateSelectedAddress: PropTypes.func.isRequired,
  organization: PropTypes.shape(organizationProp.propType).isRequired,
  orderDetails: PropTypes.shape({
    shippingSubTotal: PropTypes.number,
    symbol: PropTypes.string,
  }).isRequired,
  checkIfShippingIsDifferent: PropTypes.func.isRequired,
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
  updateUser: PropTypes.func.isRequired,
  isOnlyDigitalProducts: PropTypes.bool.isRequired,
  orderAlreadyCreated: PropTypes.string,
  isShippingDifferent: PropTypes.bool.isRequired,
  tenant: PropTypes.shape(TenantProp.propType).isRequired,
  onCompanyBillingChange: PropTypes.func.isRequired,
  billingActive: PropTypes.string.isRequired,
  errors: PropTypes.shape({
    billingAddress: PropTypes.arrayOf(PropTypes.shape({})),
    companyBilling: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  onCompanyEmailsChange: PropTypes.func.isRequired,
  companyEmails: PropTypes.shape({
    email: PropTypes.string,
    emails: PropTypes.arrayOf(PropTypes.string),
  }),
  handleBillingChange: PropTypes.func.isRequired,
  onCompanyErrors: PropTypes.func.isRequired,
  isSubscription: PropTypes.bool,
};

ShopProductCheckoutShipping.defaultProps = {
  orderAlreadyCreated: null,
  companyEmails: {
    email: '',
    emails: [],
  },
  errors: [],
  isSubscription: false,
};

export default compose(translate, withCSRF)(ShopProductCheckoutShipping);
